// /signin and /signin/finish, and the sign-in card the consent page shows (mock M2, sign-in-methods.html): the
// site's sky, live behind one white card with the wordmark. Google, a Microsoft work or school account, or the email
// link; two-step asks for a code after any of them. A link asked for from a consent page carries `next`, so it signs
// in and lands back on /connect/<id>. After an email-link sign-in, Omni offers Google or Microsoft: the one the person
// tried first, or once, to sign in faster.
import React, { useEffect, useRef, useState } from "react";
import type { Me } from "@phoneware/lens-domain";
import type { LensApi } from "../api.js";
import {
  authCode,
  authEmail,
  closedWindow,
  fasterPromptShown,
  forgetPendingLink,
  forgetSignInEmail,
  markFasterPromptShown,
  pendingLink,
  rememberPendingLink,
  rememberSignInEmail,
  rememberedSignInEmail,
  type AuthClient,
  type Method,
  type Provider,
  type SecondStep,
} from "../auth.js";
import { Icon, type IconName } from "../icons.js";
import { navigate } from "../router.js";
import { errorText, isEmail } from "../format.js";
import { LiveSky } from "../sky.js";
import { Wordmark, useTitle } from "../ui.js";
import { AppReq } from "./connect-bits.js";
import { IdpButton, LinkAlertView, NO_INVITE, PROVIDER_NAME, Refusal, addProvider, methodWords, signInProblem, type LinkAlert } from "./signin-bits.js";
import { Appearance } from "../appearance.js";

const NEXT_KEY = "lens.signin.next";

/** Only same-site paths, so a crafted link cannot send someone elsewhere after sign-in. */
export function safeNext(next: string | null): string {
  return next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/signin") ? next : "/";
}

function storedNext(): string {
  try {
    return safeNext(window.localStorage.getItem(NEXT_KEY));
  } catch {
    return "/";
  }
}

/** A consent page's own path: the only `next` the server puts on an emailed link (plan 3.5). */
const CONSENT_PATH = /^\/connect\/[^/?#]+$/;

/**
 * Emails a sign-in link and remembers, in this browser, the address and where to land. A consent page's path also goes
 * to the server, which puts it on the link, so the link comes back to the request even if this storage is gone.
 * `adding` is the provider the person tried first, offered once the link signs them in.
 */
export async function sendSignInLink(api: LensApi, address: string, next: string | null, adding: Provider | null = null): Promise<void> {
  await api.requestEmailLink(address, next && CONSENT_PATH.test(next) ? next : undefined);
  rememberSignInEmail(address);
  if (adding) rememberPendingLink(adding);
  else forgetPendingLink();
  try {
    window.localStorage.setItem(NEXT_KEY, safeNext(next));
  } catch {
    // Lands on Today instead, or on the link's own `next`.
  }
}

/** The sky and the white card every pre-sign-in page sits in. The consent page's card is a little wider. */
export function SignInCard({ children, consent, state }: { children: React.ReactNode; consent?: boolean; state?: string }): React.ReactElement {
  return (
    <main className="signin">
      <LiveSky />
      <div className={consent ? "signin-card consent" : "signin-card"} data-state={state}>
        <Wordmark variant="lockup" />
        {children}
        <Appearance />
      </div>
    </main>
  );
}

export function StateIcon({ kind, icon }: { kind?: "ok" | "warn" | "stop"; icon: IconName }): React.ReactElement {
  return (
    <span className={kind ? `state-ic ${kind}` : "state-ic"} aria-hidden="true">
      <Icon name={icon} size={20} />
    </span>
  );
}

/** The request a consent page signs someone in for: who asks, from where, and until when. */
export interface ConsentAsk {
  client: string;
  host: string;
  until: string;
}

/** The two-step code card, after the email link, Google or Microsoft. */
function CodeCard({ step, via, consent, onStartOver }: { step: SecondStep; via: Method; consent: ConsentAsk | null; onStartOver: () => void }): React.ReactElement {
  const [code, setCode] = useState("");
  const [wrong, setWrong] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function verify(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setWrong(false);
    setProblem(null);
    try {
      await step.verify(code.replace(/\D/g, ""));
    } catch (err) {
      if (authCode(err) === "auth/invalid-verification-code") setWrong(true);
      else setProblem(signInProblem(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SignInCard consent={!!consent} state="code">
      {consent ? <AppReq client={consent.client} line={`${consent.host} wants to connect to Omni`} /> : null}
      <StateIcon icon="shield-check" />
      <h1>Enter your two-step code</h1>
      <p className="signin-lede">
        Open the authenticator app you set up for Omni and enter the 6-digit code{step.email ? <> for <b>{step.email}</b></> : null}.
      </p>
      <form onSubmit={(e) => void verify(e)} noValidate>
        <label className="field">
          <span>Code</span>
          <input
            className="input code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            data-testid="signin-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </label>
        {wrong ? (
          <p className="signin-error" role="alert">
            That code didn&rsquo;t match. Codes change every 30 seconds, so enter the one your app shows now.
          </p>
        ) : null}
        {problem ? (
          <p className="signin-error" role="alert">
            {problem}
          </p>
        ) : null}
        <button className="btn primary wide" type="submit" disabled={busy} data-testid="signin-verify">
          {busy ? "Checking" : "Verify"}
        </button>
      </form>
      <p className="signin-note">
        Step one done with {methodWords(via)}
        {step.email ? (
          <>
            , as <b>{step.email}</b>
          </>
        ) : null}
        . Not you?{" "}
        <button className="link" type="button" onClick={onStartOver}>
          Start over
        </button>
      </p>
      {consent ? <RequestLasts consent={consent} /> : null}
    </SignInCard>
  );
}

function RequestLasts({ consent }: { consent: ConsentAsk }): React.ReactElement {
  return (
    <p className="signin-note">
      {consent.client}&rsquo;s request lasts 10 minutes, until {consent.until}.
    </p>
  );
}

type StartAlert = { kind: "not-invited"; provider: Provider } | { kind: "ms-closed" } | { kind: "problem"; text: string };

type Step =
  | { kind: "start"; alert: StartAlert | null }
  | { kind: "sent"; email: string }
  | { kind: "link-needed"; provider: Provider; email: string }
  | { kind: "link-sent"; provider: Provider; email: string }
  | { kind: "code"; step: SecondStep; via: Method };

/**
 * The three ways in, and what each can come to: no invite, Microsoft refusing a personal account, an email that signs in
 * with a link so far (plan 3.6: Omni emails a link and adds Google or Microsoft once it is opened), and the two-step code.
 * On a consent page (`consent`) the card carries the request and every link comes back to it.
 */
export function SignInFlow({ api, auth, next, consent }: { api: LensApi; auth: AuthClient | null; next: string | null; consent: ConsentAsk | null }): React.ReactElement {
  const [step, setStep] = useState<Step>({ kind: "start", alert: null });
  const [typed, setTyped] = useState(() => rememberedSignInEmail() ?? "");
  const [busy, setBusy] = useState<Provider | "link" | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const tid = consent ? "consent" : "signin";
  const box = consent ? <AppReq client={consent.client} line={`${consent.host} wants to connect to Omni`} /> : null;
  const lasts = consent ? <RequestLasts consent={consent} /> : null;
  const startOver = () => setStep({ kind: "start", alert: null });

  async function continueWith(provider: Provider): Promise<void> {
    if (!auth) return;
    setBusy(provider);
    setFieldError(null);
    try {
      const result = await auth.signInWithProvider(provider);
      // Signed in: the page moves on once Omni has read who it is.
      if (result.kind === "code") setStep({ kind: "code", step: result.step, via: provider });
    } catch (err) {
      const code = authCode(err);
      const email = authEmail(err);
      if (code === "auth/admin-restricted-operation") setStep({ kind: "start", alert: { kind: "not-invited", provider } });
      else if (code === "auth/account-exists-with-different-credential" && email) setStep({ kind: "link-needed", provider, email });
      else if (closedWindow(err)) setStep({ kind: "start", alert: provider === "microsoft" ? { kind: "ms-closed" } : null });
      else setStep({ kind: "start", alert: { kind: "problem", text: signInProblem(err) } });
    } finally {
      setBusy(null);
    }
  }

  async function send(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const address = typed.trim().toLowerCase();
    if (!isEmail(address)) {
      setFieldError("Enter the email your invite went to.");
      return;
    }
    setBusy("link");
    setFieldError(null);
    try {
      await sendSignInLink(api, address, next);
      setStep({ kind: "sent", email: address });
    } catch (err) {
      setFieldError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function sendPending(provider: Provider, email: string): Promise<void> {
    setBusy("link");
    setFieldError(null);
    try {
      // A consent page's link comes back to the request; Google or Microsoft is added later, in Settings.
      await sendSignInLink(api, email, next, consent ? null : provider);
      setStep({ kind: "link-sent", provider, email });
    } catch (err) {
      setFieldError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  if (step.kind === "code") return <CodeCard step={step.step} via={step.via} consent={consent} onStartOver={startOver} />;

  if (step.kind === "sent") {
    return (
      <SignInCard consent={!!consent} state="sent">
        {box}
        <div data-testid={`${tid}-sent`}>
          <StateIcon icon="mail" />
          <h1>Check your email</h1>
          <p className="signin-lede">
            If <b>{step.email}</b> is invited to Omni, a sign-in link is on its way. Open it in this browser{consent ? " and it brings you back to this request" : ""}. It works once.
          </p>
          <p className="signin-note">
            <button className="link" type="button" data-testid={`${tid}-other-email`} onClick={startOver}>
              Use a different email
            </button>
          </p>
          {lasts}
        </div>
      </SignInCard>
    );
  }

  if (step.kind === "link-needed") {
    const P = PROVIDER_NAME[step.provider];
    return (
      <SignInCard consent={!!consent} state="link-needed">
        {box}
        <div data-testid="signin-link-needed">
          <StateIcon icon="link" />
          <h1>This email signs in with a link</h1>
          <p className="signin-lede">
            {P} says you&rsquo;re <b>{step.email}</b>. Omni already signs that email in with a link, so open one from your inbox first.{" "}
            {consent ? `It brings you back to this request. Add ${P} in Settings, Sign-in afterwards.` : `Then one click adds ${P}, and Continue with ${P} works from then on.`}
          </p>
          {fieldError ? (
            <p className="signin-error" role="alert">
              {fieldError}
            </p>
          ) : null}
          <button className="btn primary wide" type="button" disabled={busy !== null} data-testid="signin-send-pending" onClick={() => void sendPending(step.provider, step.email)}>
            <Icon name="mail" size={15} />
            {busy === "link" ? "Sending" : "Email me the link"}
          </button>
          <p className="signin-note">
            <button className="link" type="button" onClick={startOver}>
              Sign in another way
            </button>
          </p>
          {lasts}
        </div>
      </SignInCard>
    );
  }

  if (step.kind === "link-sent") {
    return (
      <SignInCard consent={!!consent} state="link-sent">
        {box}
        <div data-testid="signin-link-sent">
          <StateIcon icon="mail" />
          <h1>Check your email</h1>
          <p className="signin-lede">
            A sign-in link is on its way to <b>{step.email}</b>. Open it in this browser and {consent ? "you\u2019re back at this request." : `Omni asks you to add ${PROVIDER_NAME[step.provider]}.`} It works once.
          </p>
          <p className="signin-note">
            <button className="link" type="button" onClick={startOver}>
              Start over
            </button>
          </p>
          {lasts}
        </div>
      </SignInCard>
    );
  }

  const alert = step.alert;
  return (
    <SignInCard consent={!!consent} state={consent ? "consent-signin" : "signin"}>
      {box}
      <form onSubmit={(e) => void send(e)} noValidate data-testid={consent ? "consent-signin" : undefined}>
        <h1>{consent ? "Sign in to choose" : "Sign in to Omni"}</h1>
        <p className="signin-lede">
          {consent ? `Sign in to Omni. Then you\u2019ll see what ${consent.client} is asking for, and allow or deny it.` : "The Phoneware dealer portal. Use the email your invite went to."}
        </p>
        {alert?.kind === "not-invited" ? (
          <Refusal kind="stop" icon="user-x" title="You have not been invited to Omni" testId="signin-not-invited">
            Nobody has invited the email on that {PROVIDER_NAME[alert.provider]} account. Use the account or email your invite went to.
          </Refusal>
        ) : alert?.kind === "ms-closed" ? (
          <Refusal kind="warn" icon="info" title="Microsoft didn&rsquo;t sign you in" testId="signin-ms-closed">
            Omni takes work or school Microsoft accounts only. For a personal one, like outlook.com or hotmail.com, use the email link below.
          </Refusal>
        ) : alert?.kind === "problem" ? (
          <Refusal kind="stop" icon="triangle-alert" title="That didn&rsquo;t work" testId="signin-problem">
            {alert.text}
          </Refusal>
        ) : null}
        <div className="signin-actions">
          <IdpButton provider="google" label="Continue with Google" testId={`${tid}-google`} disabled={!auth || busy !== null} onClick={() => void continueWith("google")} />
          <IdpButton provider="microsoft" label="Continue with Microsoft" testId={`${tid}-microsoft`} disabled={!auth || busy !== null} onClick={() => void continueWith("microsoft")} />
        </div>
        <p className="or">or</p>
        <label className="field">
          <span>Email</span>
          <input className="input" type="email" name="email" autoComplete="email" data-testid={`${tid}-email`} value={typed} onChange={(e) => setTyped(e.target.value)} />
        </label>
        {fieldError ? (
          <p className="signin-error" role="alert">
            {fieldError}
          </p>
        ) : null}
        <button className="btn primary wide" type="submit" disabled={busy !== null} data-testid={`${tid}-send`}>
          <Icon name="mail" size={15} />
          {busy === "link" ? "Sending" : "Email me a sign-in link"}
        </button>
        {lasts}
        <p className="signin-note">{NO_INVITE}</p>
      </form>
    </SignInCard>
  );
}

export function SignIn({ api, auth, next }: { api: LensApi; auth: AuthClient | null; next: string | null }): React.ReactElement {
  useTitle("Sign in");
  return <SignInFlow api={api} auth={auth} next={next} consent={null} />;
}

type Finish =
  | { kind: "working" }
  | { kind: "ask-email" }
  | { kind: "failed"; message: string }
  | { kind: "not-a-link" }
  | { kind: "code"; step: SecondStep; next: string }
  | { kind: "after"; next: string }
  | { kind: "add"; provider: Provider; next: string; alert: LinkAlert | null }
  | { kind: "faster"; next: string; alert: LinkAlert | null }
  | { kind: "linked"; provider: Provider; next: string };

function failureText(err: unknown): string {
  const code = authCode(err);
  if (code === "auth/invalid-email") return "That email does not match the link. Use the address the link was sent to.";
  if (code === "auth/invalid-action-code" || code === "auth/expired-action-code") return "This sign-in link has expired or was already used.";
  return errorText(err);
}

/**
 * /signin/finish: completes the emailed link, asks for the two-step code when two-step is on, then, once Omni has read
 * who signed in, offers the provider they tried first ("Add Google") or, once, a faster way in. Neither while acting as
 * someone or on the way back to a consent page.
 */
export function SignInFinish({ auth, me, refused, onSignedIn }: { auth: AuthClient; me: Me | null; refused: boolean; onSignedIn: () => void }): React.ReactElement {
  useTitle("Signing in");
  const [state, setState] = useState<Finish>({ kind: "working" });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  async function complete(address: string) {
    setState({ kind: "working" });
    try {
      const result = await auth.completeSignIn(address, window.location.href);
      forgetSignInEmail();
      // A link sent from a consent page names its own way back; otherwise this browser remembered where to land.
      const fromLink = new URLSearchParams(window.location.search).get("next");
      const next = fromLink ? safeNext(fromLink) : storedNext();
      try {
        window.localStorage.removeItem(NEXT_KEY);
      } catch {
        // Nothing stored.
      }
      if (result.kind === "code") {
        setState({ kind: "code", step: result.step, next });
        return;
      }
      onSignedIn();
      setState({ kind: "after", next });
    } catch (err) {
      setState({ kind: "failed", message: failureText(err) });
    }
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!auth.isSignInLink(window.location.href)) {
      if (auth.email()) navigate("/", { replace: true });
      else setState({ kind: "not-a-link" });
      return;
    }
    const remembered = rememberedSignInEmail();
    if (remembered) void complete(remembered);
    else setState({ kind: "ask-email" });
    // Runs once per page load: the link's code works once.
  }, []);

  // Signed in: what to offer, once Omni has read who it is. A refusal is the portal's to show, on the page it lands on.
  useEffect(() => {
    if (state.kind !== "after" || (!me && !refused)) return;
    const next = state.next;
    const providers = auth.linkedProviders();
    const adding = pendingLink();
    if (refused || me?.actingAs || CONSENT_PATH.test(next)) {
      forgetPendingLink();
      navigate(next, { replace: true });
    } else if (adding && !providers.some((p) => p.provider === adding)) {
      setState({ kind: "add", provider: adding, next, alert: null });
    } else if (!fasterPromptShown() && providers.length === 0) {
      markFasterPromptShown();
      setState({ kind: "faster", next, alert: null });
    } else {
      forgetPendingLink();
      navigate(next, { replace: true });
    }
  }, [state, me, refused]);

  const signedInAs = auth.email() ?? "";
  const leave = (next: string) => {
    forgetPendingLink();
    navigate(next, { replace: true });
  };

  async function add(provider: Provider, next: string, from: "add" | "faster"): Promise<void> {
    setBusy(true);
    const outcome = await addProvider(auth, provider, signedInAs);
    setBusy(false);
    if (outcome.kind === "added") {
      forgetPendingLink();
      setState({ kind: "linked", provider, next });
    } else if (outcome.kind === "refused") {
      setState(from === "add" ? { kind: "add", provider, next, alert: outcome.alert } : { kind: "faster", next, alert: outcome.alert });
    }
  }

  if (state.kind === "code") {
    const next = state.next;
    return (
      <CodeCard
        step={{
          email: state.step.email,
          verify: async (code) => {
            await state.step.verify(code);
            onSignedIn();
            setState({ kind: "after", next });
          },
        }}
        via="link"
        consent={null}
        onStartOver={() => navigate("/signin", { replace: true })}
      />
    );
  }
  if (state.kind === "add") {
    const P = PROVIDER_NAME[state.provider];
    return (
      <SignInCard state="add">
        <div data-testid="signin-add-card">
          <StateIcon kind="ok" icon="circle-check" />
          <h1>
            You&rsquo;re signed in. Add {P}?
          </h1>
          <p className="signin-lede">
            Signed in as <b>{signedInAs}</b> with the email link. Add {P} and Continue with {P} signs you straight in next time.
          </p>
          {state.alert ? <LinkAlertView alert={state.alert} omniEmail={signedInAs} /> : null}
          <div className="signin-actions">
            <IdpButton provider={state.provider} label={`Add ${P}`} testId="signin-add" disabled={busy} onClick={() => void add(state.provider, state.next, "add")} />
            <button className="btn wide" type="button" data-testid="signin-not-now" onClick={() => leave(state.next)}>
              Not now, open Omni
            </button>
          </div>
          <p className="signin-note">
            Choose the {P} account for {signedInAs}.
          </p>
        </div>
      </SignInCard>
    );
  }
  if (state.kind === "faster") {
    return (
      <SignInCard state="faster">
        <div data-testid="signin-faster">
          <StateIcon kind="ok" icon="circle-check" />
          <h1>Sign in faster next time?</h1>
          <p className="signin-lede">
            You&rsquo;re signed in as <b>{signedInAs}</b>. Add Google or Microsoft, and Continue with it signs you straight in. No email to wait for.
          </p>
          {state.alert ? <LinkAlertView alert={state.alert} omniEmail={signedInAs} /> : null}
          <div className="signin-actions">
            <IdpButton provider="google" label="Add Google" testId="signin-add-google" disabled={busy} onClick={() => void add("google", state.next, "faster")} />
            <IdpButton provider="microsoft" label="Add Microsoft" testId="signin-add-microsoft" disabled={busy} onClick={() => void add("microsoft", state.next, "faster")} />
            <button className="btn wide" type="button" data-testid="signin-not-now" onClick={() => leave(state.next)}>
              Not now, open Omni
            </button>
          </div>
          <p className="signin-note">Omni asks this once. Settings, Sign-in has both any time.</p>
        </div>
      </SignInCard>
    );
  }
  if (state.kind === "linked") {
    const P = PROVIDER_NAME[state.provider];
    return (
      <SignInCard state="linked">
        <div data-testid="signin-linked">
          <StateIcon kind="ok" icon="circle-check" />
          <h1>{P} is added</h1>
          <p className="signin-lede">
            Next time, Continue with {P} signs you in as <b>{signedInAs}</b>. The email link still works too.
          </p>
          <button className="btn primary wide" type="button" data-testid="signin-open" onClick={() => leave(state.next)}>
            <Icon name="arrow-right" size={15} />
            Open Omni
          </button>
          <p className="signin-note">Settings, Sign-in lists how you sign in.</p>
        </div>
      </SignInCard>
    );
  }
  if (state.kind === "ask-email") {
    return (
      <SignInCard>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const address = email.trim().toLowerCase();
            if (isEmail(address)) void complete(address);
          }}
        >
          <h1>Confirm your email</h1>
          <p className="signin-lede">This link was opened in a different browser. Enter the email it was sent to.</p>
          <label className="field">
            <span>Email</span>
            <input className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <button className="btn primary wide" type="submit" disabled={!isEmail(email)}>
            Sign in
          </button>
        </form>
      </SignInCard>
    );
  }
  if (state.kind === "failed" || state.kind === "not-a-link") {
    return (
      <SignInCard>
        <h1>That link did not work</h1>
        <p className="signin-lede">{state.kind === "failed" ? state.message : "This is not an Omni sign-in link."} Ask for a new one and open it in this browser.</p>
        <button className="btn primary wide" type="button" onClick={() => navigate("/signin", { replace: true })}>
          <Icon name="mail" size={15} />
          Get a new sign-in link
        </button>
      </SignInCard>
    );
  }
  return (
    <SignInCard>
      <h1>Signing you in</h1>
      <p className="signin-lede">One moment.</p>
    </SignInCard>
  );
}
